import {
    Alert,
    Box,
    Container,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    List,
    ListItem,
    Modal,
    ModalProps,
    Paper,
    Radio,
    RadioGroup,
    SvgIcon,
    Typography,
    useTheme,
} from '@mui/material';
import { Stack, styled } from '@mui/system';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { grey } from '@mui/material/colors';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from 'src/components/button';
import { ReactComponent as QuestionIcon } from 'src/assets/svg/circled-question-mark.svg';
import { StyledTooltip } from 'src/layout/sidebar/parts/nav-button';

import { ReactComponent as IconSettings } from '../../../../assets/svg/preferences.svg';
import { ReactComponent as IconSun } from '../../../../assets/svg/sun.svg';
import { ReactComponent as IconMoon } from '../../../../assets/svg/moon.svg';
import { ReactComponent as IconMop } from '../../../../assets/svg/mop-dark.svg';
import { ReactComponent as IconKitchen } from '../../../../assets/svg/kitchen-dark.svg';
import { ReactComponent as IconShower } from '../../../../assets/svg/shower-dark.svg';
import { ReactComponent as IconClose } from '../../../../assets/svg/cross.svg';
import { useHousekeepingOptIn } from '../../hooks/use-housekeeping-opt-in';
import { AssignedTime } from '../enum/inspection-assigned-time';
import { CleanType } from '../enum/clean-type';
import { UnitType } from '../enum/unit-type';

type Props = Omit<ModalProps, 'children' | 'onClose'> & {
    maintenanceId: string;
    academicYear: string;
    unitType: UnitType;
    onClose: (reloadHousekeeping: boolean) => void;
};

const TooltipBulletList = styled(List)(({ theme }) => ({
    margin: 0,
    paddingLeft: '20px',
    listStyleType: 'disc',
    color: theme.palette.text.primary,
    fontSize: '0.85rem',
    '& .MuiListItem-root': {
        display: 'list-item',
        padding: '2px 0',
    },
}));

const WhatsIncludedTooltip: FC<{ content: React.ReactNode }> = ({ content }) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <StyledTooltip
            title={
                <Box>
                    <Typography variant="overline" sx={{ color: theme.palette.text.primary }}>
                        {t('housekeeping_whats_included_title')}
                    </Typography>
                    {content}
                </Box>
            }
            placement="right-start"
            PopperProps={{
                modifiers: [
                    {
                        name: 'flip',
                        enabled: true,
                        options: {
                            fallbackPlacements: ['left-start', 'top-start', 'bottom-start'],
                        },
                    },
                    {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                            padding: 8,
                        },
                    },
                ],
            }}
            componentsProps={{
                tooltip: {
                    sx: {
                        borderRadius: 0,
                        padding: '12px 16px',
                        maxWidth: '60vw',
                        wordBreak: 'break-word',
                    },
                },
            }}
        >
            <IconButton
                size="small"
                sx={{ padding: 0 }}
                aria-label={t('housekeeping_whats_included_aria_label')}
            >
                <QuestionIcon />
            </IconButton>
        </StyledTooltip>
    );
};

export const HousekeepingPreferencesModal: FC<Props> = ({
    open,
    maintenanceId,
    unitType,
    onClose,
    academicYear,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { palette } = theme;
    const { register, handleSubmit, formState, watch } = useForm({
        resolver: yupResolver(
            object({
                preferredCleaningTime: string().oneOf(Object.values(AssignedTime)),
                cleanType: string().oneOf(Object.values(CleanType)),
            })
        ),
    });

    const selectedCleaningTime = watch('preferredCleaningTime');
    const selectedCleanType = watch('cleanType');

    const cleanTime =
        unitType !== UnitType.CLUSTER ? t('housekeeping_preferences_20_minute_clean_time') : '';

    const {
        isLoading,
        isError,
        reset,
        mutate: optIn,
    } = useHousekeepingOptIn(maintenanceId, { onSuccess: () => onClose && onClose(true) });

    const cleaningTimes = [
        {
            Icon: IconSun,
            value: AssignedTime.Morning,
            label: t('housekeeping_preferences_cleaning_time_morning'),
        },
        {
            Icon: IconMoon,
            value: AssignedTime.Afternoon,
            label: t('housekeeping_preferences_cleaning_time_afternoon'),
        },
    ];

    const cleaningTypes = [
        {
            Icon: IconMop,
            value: CleanType.STANDARD,
            text: t('housekeeping_preferences_standard_title'),
            subtext: (
                <Trans
                    i18nKey="housekeeping_preferences_standard_subtitle"
                    values={{ cleanTime }}
                />
            ),
            Included: (
                <TooltipBulletList>
                    {[
                        'housekeeping_standard_clean_kitchen',
                        'housekeeping_standard_clean_bathroom',
                        'housekeeping_standard_clean_living_area',
                        'housekeeping_standard_clean_floors',
                    ].map((key) => {
                        const translation = t(key);
                        const [label, ...rest] = translation.split(':');
                        return (
                            <ListItem key={key}>
                                <strong>{label}:</strong> {rest.join(':').trim()}
                            </ListItem>
                        );
                    })}
                </TooltipBulletList>
            ),
        },
        {
            Icon: IconShower,
            value: CleanType.BATHROOM,
            text: t('housekeeping_preferences_bathroom_title'),
            subtext: (
                <Trans
                    i18nKey="housekeeping_preferences_bathroom_subtitle"
                    values={{ cleanTime }}
                />
            ),
            Included: (
                <TooltipBulletList>
                    {[
                        'housekeeping_bathroom_clean_toilet',
                        'housekeeping_bathroom_clean_sink',
                        'housekeeping_bathroom_clean_shower',
                        'housekeeping_bathroom_clean_drains',
                        'housekeeping_bathroom_clean_mirrors',
                        'housekeeping_bathroom_clean_cupboards',
                        'housekeeping_bathroom_clean_shelving',
                        'housekeeping_bathroom_clean_grouting',
                        'housekeeping_bathroom_clean_floors',
                    ].map((key) => (
                        <ListItem key={key}>{t(key)}</ListItem>
                    ))}
                </TooltipBulletList>
            ),
        },
        ...(unitType !== UnitType.CLUSTER
            ? [
                  {
                      Icon: IconKitchen,
                      value: CleanType.KITCHEN,
                      text: t('housekeeping_preferences_kitchen_title'),
                      subtext: t('housekeeping_preferences_kitchen_subtitle'),
                      Included: (
                          <TooltipBulletList>
                              {[
                                  'housekeeping_kitchen_clean_countertops',
                                  'housekeeping_kitchen_clean_sink',
                                  'housekeeping_kitchen_clean_dishes',
                                  'housekeeping_kitchen_clean_hob',
                                  'housekeeping_kitchen_clean_oven',
                                  'housekeeping_kitchen_clean_cupboards',
                                  'housekeeping_kitchen_clean_floors',
                              ].map((key) => (
                                  <ListItem key={key}>{t(key)}</ListItem>
                              ))}
                          </TooltipBulletList>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <Modal open={open} onClose={() => onClose(false)} keepMounted={false}>
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Container maxWidth="md">
                    <Paper css={{ borderRadius: 0 }}>
                        <Stack
                            alignItems="stretch"
                            width="100%"
                            padding={{ xs: '32px 16px', md: '36px' }}
                            component="form"
                            gap="32px"
                            onSubmit={handleSubmit((values) => {
                                reset();
                                optIn({
                                    cleanType: values.cleanType,
                                    preferredCleaningTime: values.preferredCleaningTime,
                                    academicYear,
                                });
                            })}
                        >
                            <Stack direction="row" justifyContent="space-between">
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap="16px"
                                    css={{
                                        textDecoration: 'none',
                                        color: palette.common.black,
                                    }}
                                >
                                    <SvgIcon>
                                        <IconSettings />
                                    </SvgIcon>

                                    <Typography
                                        variant="h3"
                                        flex={1}
                                        css={{ textTransform: 'uppercase' }}
                                    >
                                        {t('housekeeping_preferences_modal_title')}
                                    </Typography>
                                </Stack>
                                <IconButton onClick={() => onClose(false)}>
                                    <IconClose />
                                </IconButton>
                            </Stack>
                            <FormControl
                                css={{
                                    rowGap: '12px',
                                }}
                            >
                                <FormLabel
                                    id="housekeeping-time-preference"
                                    css={{
                                        textTransform: 'uppercase',
                                        padding: 0,
                                        '&.Mui-focused': {
                                            color: `${theme.palette.text.secondary}`,
                                        },
                                    }}
                                >
                                    {t('housekeeping_preferences_modal_preferred_time')}
                                </FormLabel>
                                <RadioGroup
                                    row
                                    aria-labelledby="housekeeping-time-preference"
                                    name="row-radio-buttons-group"
                                    css={{ columnGap: '12px' }}
                                >
                                    {cleaningTimes.map(({ Icon, value, label }) => (
                                        <FormControlLabel
                                            key={`housekeeping-time-preference-${value}`}
                                            {...register('preferredCleaningTime')}
                                            value={value}
                                            control={
                                                <Radio
                                                    disableRipple={false}
                                                    checkedIcon={
                                                        <Icon
                                                            height="20px"
                                                            fontSize={
                                                                theme.typography.body2.fontSize
                                                            }
                                                        />
                                                    }
                                                    icon={
                                                        <Icon
                                                            height="20px"
                                                            fontSize={
                                                                theme.typography.body2.fontSize
                                                            }
                                                        />
                                                    }
                                                />
                                            }
                                            label={label}
                                            css={{
                                                margin: 0,
                                                flexGrow: 1,
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                padding: '12px',
                                                border: `1px solid ${
                                                    selectedCleaningTime === value
                                                        ? theme.palette.grey[600]
                                                        : theme.palette.grey[400]
                                                }`,
                                                backgroundColor:
                                                    selectedCleaningTime === value
                                                        ? theme.palette.grey[200]
                                                        : 'inherit',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.grey[200],
                                                },
                                            }}
                                        />
                                    ))}
                                </RadioGroup>
                                <Typography
                                    color={grey[600]}
                                    variant="subtitle2"
                                    css={{ fontStyle: 'italic' }}
                                >
                                    {t('housekeeping_preferences_cleaning_disclaimer')}
                                </Typography>
                            </FormControl>
                            <FormControl css={{ rowGap: '12px' }}>
                                <FormLabel
                                    id="housekeeping-preferences-clean-type"
                                    css={{
                                        textTransform: 'uppercase',
                                        '&.Mui-focused': {
                                            color: `${theme.palette.text.secondary}`,
                                        },
                                    }}
                                >
                                    {t('housekeeping_preferences_modal_preferred_clean_type')}
                                </FormLabel>
                                <RadioGroup
                                    aria-labelledby="housekeeping-preferences-clean-type"
                                    css={{ rowGap: '12px' }}
                                    name="row-radio-buttons-group"
                                >
                                    {cleaningTypes.map(
                                        ({ Icon, value, text, subtext, Included }) => (
                                            <FormControlLabel
                                                key={`housekeeping-clean-types-${value}`}
                                                {...register('cleanType')}
                                                css={{
                                                    flexDirection: 'row-reverse',
                                                    justifyContent: 'space-between',
                                                    margin: 0,
                                                    padding: '20px 16px',
                                                    border: `1px solid ${selectedCleanType === value ? theme.palette.grey[600] : theme.palette.grey[400]}`,
                                                    backgroundColor:
                                                        selectedCleanType === value
                                                            ? theme.palette.grey[200]
                                                            : 'inherit',
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.grey[200],
                                                    },
                                                }}
                                                value={value}
                                                control={<Radio />}
                                                label={
                                                    <Stack
                                                        direction="row"
                                                        columnGap="12px"
                                                        alignItems="center"
                                                    >
                                                        <Icon height="20px" />
                                                        <Stack rowGap="4px">
                                                            <Stack
                                                                direction="row"
                                                                columnGap="6px"
                                                                alignItems="center"
                                                            >
                                                                <Typography
                                                                    variant="h4"
                                                                    css={{
                                                                        textTransform: 'uppercase',
                                                                    }}
                                                                >
                                                                    {text}
                                                                </Typography>
                                                                <WhatsIncludedTooltip
                                                                    content={Included}
                                                                />
                                                            </Stack>
                                                            <Typography color={grey[600]}>
                                                                {subtext}
                                                            </Typography>
                                                        </Stack>
                                                    </Stack>
                                                }
                                            />
                                        )
                                    )}
                                </RadioGroup>
                            </FormControl>
                            {isError && (
                                <Alert severity="error" css={{ borderRadius: 0 }}>
                                    {t('housekeeping_preferences_submit_error')}
                                </Alert>
                            )}
                            <Button
                                disabled={!formState.isValid}
                                loading={isLoading}
                                fullWidth
                                type="submit"
                                css={{
                                    backgroundColor: palette.primary.main,
                                    color: 'white',
                                    textTransform: 'uppercase',
                                }}
                            >
                                {t('housekeeping_preferences_submit')}
                            </Button>
                        </Stack>
                    </Paper>
                </Container>
            </Box>
        </Modal>
    );
};
